// Factory zustand provider-agnostic. Compone lo stato iniziale e le slice,
// delegando ogni I/O al MailAdapter passato. Replica ESATTAMENTE stato e
// azioni dell'originale gmailStore.ts.
import { create } from 'zustand';
import type { MailAdapter } from './mailAdapter';
import type { MailStore, MailState } from './storeTypes';
import { createSyncSlice } from './slices/syncSlice';
import { createThreadsSlice } from './slices/threadsSlice';
import { createMessagesSlice } from './slices/messagesSlice';
import { createComposeSlice } from './slices/composeSlice';
import { createTemplateSlice } from './slices/templateSlice';

function buildInitialState(adapter: MailAdapter): MailState {
  return {
    syncStatus: 'idle',
    syncError: null,
    lastSyncAt: null,
    threads: [],
    threadsLoading: false,
    threadsError: null,
    activeLabel: 'INBOX',
    searchQuery: '',
    viewMode: (typeof localStorage !== 'undefined' && localStorage.getItem(adapter.storagePrefix) === 'list' ? 'list' : 'split') as 'split' | 'list',
    selectedThreadId: null,
    selectedMessages: [],
    messagesLoading: false,
    composing: false,
    composeMinimized: false,
    currentDraftId: null,
    savingDraft: false,
    replyMode: 'reply' as const,
    replyingToMessageId: null,
    replyingToThreadId: null,
    composePrefill: null,
    templates: [],
    templatesLoading: false,
    labels: {},
    drafts: {},
    editingDraft: null,
    unreadCount: 0,
    keptUnreadThreadIds: [],
    _pollingInterval: null,
  };
}

export function createMailStore(adapter: MailAdapter) {
  const initialState = buildInitialState(adapter);

  return create<MailStore>((set, get) => ({
    ...initialState,

    ...createSyncSlice(set, get, adapter),
    ...createThreadsSlice(set, get, adapter),
    ...createMessagesSlice(set, get, adapter),
    ...createComposeSlice(set, get, adapter),
    ...createTemplateSlice(set, get, adapter),

    // Filters
    setActiveLabel: (label) => set({ activeLabel: label, keptUnreadThreadIds: [] }),
    setSearchQuery: (query) => set({ searchQuery: query }),

    // Layout
    setViewMode: (mode) => {
      try { localStorage.setItem(adapter.storagePrefix, mode); } catch {}
      set({ viewMode: mode });
    },

    // Reset
    reset: () => {
      get().stopPolling();
      set(initialState);
    },
  }));
}
